// ─────────────────────────────────────────────────────────────────────────
// financeApprovalEngine — pure, framework-free rate math + record resolution
// for the config-driven Finance Approval flow (submissionManager /
// onboardingManager / cfo). No JSX, no antd, no fetching — so it is unit
// testable in isolation and shared identically across all three approval
// surfaces and both contract types (W2 / C2C).
//
// Model (confirmed against zinnext-dev-api-v1):
//   Client side (context only):
//     netClientRate  = clientJobRate − clientJobRate × clientVms%
//   Proposed side (feeds margin):
//     discountVms    = proposedRate × clientVms%        (clients.vmsFee)
//     netProposed    = proposedRate − discountVms
//   Employer side (feeds margin):
//     C2C: employerDeduction = employerRate × employerVms%   (submission employer)
//     W2 : employerDeduction = employerRate × locationTax%   (employerLocationTax)
//     netEmployer    = employerRate − employerDeduction
//   Outcome:
//     totalMargin    = netProposed − netEmployer − referralRate
//   (referralRate is 0 unless a referral is present AND the type shows it.)
// ─────────────────────────────────────────────────────────────────────────

import {
  currencySymbol,
  rateUnitLabel as unitLabel,
  rateUnitShort as unitShort,
  formatMoney as formatMoneyShared,
  toNumber,
} from '../../utils/currency.js';

export const CONTRACT_W2 = 'W2';
export const CONTRACT_C2C = 'C2C';

// Last-resort display defaults. They are the FLOOR of the resolution chains
// below, not a policy: a caller (the panel, from financeApprovalConfig) passes
// its own `defaults` so the fallback unit/currency is admin-configurable per
// stage, and these only apply when nothing at all was configured.
export const ENGINE_DEFAULTS = { rateUnit: 'hr', currency: '$' };

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const truthy = (value) => value === true || value === 'true' || value === 1 || value === '1';

// hasValue/firstDefined — `??` only skips null/undefined, so a field read
// straight off a record (not through pick(), which already does this) stops
// a real fallback dead if the stored value is "" — e.g. a client saved before
// vmsPercentage existed still has SOME doc with the key present but empty,
// which no plain `??` chain would ever fall through past to reach vmsFee.value.
const hasValue = (value) => value !== undefined && value !== null && value !== '';
const firstDefined = (...values) => values.find(hasValue);

const round2 = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;

// last(array) — the newest entry of an append-only history array (proposedRate,
// candidateBudget, submission_current_employer …). Non-arrays pass through.
export function lastEntry(value) {
  if (Array.isArray(value)) return value.length ? value[value.length - 1] : {};
  return value ?? {};
}

// idOf — normalize a Mongo id in any of its wire shapes to a plain string.
export const idOf = (v) => String(v?.$oid ?? v?._id?.$oid ?? v?._id ?? v?.id ?? v ?? '');

// flattenGroups — /admin/form-groups returns groups[].fields[] as
// { field, value }. Flatten to { <field>: value }. Dotted field names
// ("candidateBudget.candidateBudgetStart") are preserved verbatim — pick()
// reads them natively. This is the authoritative record source on backends
// with no by-id detail route.
export function flattenGroups(groups = []) {
  const flat = {};
  groups.forEach((group) => {
    (group.fields ?? []).forEach((f) => {
      const key = f.field ?? f.fieldName ?? f.name;
      if (key && f.value !== undefined && f.value !== null && f.value !== '') {
        flat[key] = f.value;
      }
    });
  });
  return flat;
}

// unflattenRecord — the exact inverse of flattenGroups. Configured field keys
// are dotted when the value is stored nested ("jobPayDetails.clientBudgetStart",
// "vmsFeeHistory.vmsPercentage"); rebuild the real nested shape so a record
// assembled from form-groups reads identically to a raw Mongo document.
// Generic — no field name appears here, so newly configured nested fields work
// with no change. A scalar never clobbers a container another key already built.
export function unflattenRecord(flat = {}) {
  const out = {};
  for (const [key, value] of Object.entries(flat)) {
    const parts = String(key).split('.');
    let cursor = out;
    for (let i = 0; i < parts.length - 1; i += 1) {
      const part = parts[i];
      if (!cursor[part] || typeof cursor[part] !== 'object' || Array.isArray(cursor[part])) {
        cursor[part] = {};
      }
      cursor = cursor[part];
    }
    const leaf = parts[parts.length - 1];
    if (cursor[leaf] && typeof cursor[leaf] === 'object' && !Array.isArray(cursor[leaf])) continue;
    cursor[leaf] = value;
  }
  return out;
}

// findEmployerRow — the employer (addRow) group, identified by carrying an
// `employerId` field rather than by its label, so renaming the group in admin
// can't break resolution. Returns { employerId, location, tax, vmsCommission… }.
export function findEmployerRow(groups = []) {
  const group = groups.find((g) => (g.fields ?? []).some((f) => (f.field ?? f.name) === 'employerId'));
  if (!group) return {};
  const rows = Array.isArray(group.rows) ? group.rows : null;
  if (rows?.length) return rows[rows.length - 1] ?? {};
  const row = {};
  (group.fields ?? []).forEach((f) => {
    const key = f.field ?? f.name;
    if (key && f.value !== undefined && f.value !== null && f.value !== '') row[key] = f.value;
  });
  return row;
}

// pick(record, container[, key]) — read a value that may be stored as ANY of:
//   • a history ARRAY   record.proposedRate = [{…}, {…}]   → newest entry
//   • a flat OBJECT     record.proposedRate = {…}
//   • flattened DOTTED  record['proposedRate.candidateProposedRate']  (detail
//     header record shape) or record['proposedRate.0.candidateProposedRate']
//   • a scalar          record.contractType = 'W2'   (call with no key)
// Returns undefined when nothing is present, so callers can ?? fall back.
export function pick(record, container, key) {
  if (!record) return undefined;
  const c = record[container];
  if (key === undefined) {
    // scalar container (e.g. contractType, city)
    if (c !== undefined && c !== null && c !== '') return c;
    return undefined;
  }
  // array → newest entry; object → direct
  if (Array.isArray(c) && c.length) {
    const v = c[c.length - 1]?.[key];
    if (v !== undefined && v !== null && v !== '') return v;
  } else if (c && typeof c === 'object') {
    const v = c[key];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  // flattened dotted key(s)
  const dotted = record[`${container}.${key}`];
  if (dotted !== undefined && dotted !== null && dotted !== '') return dotted;
  const dotted0 = record[`${container}.0.${key}`];
  if (dotted0 !== undefined && dotted0 !== null && dotted0 !== '') return dotted0;
  return undefined;
}

// normalizeContract — collapse the many spellings the DB carries
// (contractType "C2C"/"c2c", onboardingType "C2C", "Corp to Corp", "W2"…)
// into CONTRACT_W2 / CONTRACT_C2C. Defaults to C2C (the referral-bearing,
// employer-VMS path) only when nothing W2-ish is present.
export function normalizeContract(raw) {
  const text = String(raw ?? '').trim().toUpperCase().replace(/[\s_-]/g, '');
  if (text === 'W2' || text.includes('W2')) return CONTRACT_W2;
  return CONTRACT_C2C;
}

// pct(base, percent) — deduction amount for a percentage rate.
function pct(base, percent) {
  return round2((num(base) * num(percent)) / 100);
}

// ── Record resolution ──────────────────────────────────────────────────────
// resolveFinanceInputs — pull every rate/percent the engine needs off a
// resolved record ({ submission, job, client, employer, engagement }) plus
// optional lookups (clientVmsFee, employerLocationTax rows). Every source is
// defensive: missing joins resolve to 0 rather than throwing, so a partial
// record still renders a coherent (if zeroed) breakdown.
//
// `defaults` ({ rateUnit, currency }) is the tail of the unit/currency
// resolution chains — supplied by the caller from financeApprovalConfig so the
// fallback is admin config rather than a literal buried in here.
export function resolveFinanceInputs(record = {}, lookups = {}, defaults = {}) {
  const fallback = { ...ENGINE_DEFAULTS, ...(defaults ?? {}) };
  const submission = record.submission ?? record ?? {};
  const job = record.job ?? {};
  const client = record.client ?? {};
  const engagement = record.engagement ?? {};

  const contractType = normalizeContract(
    pick(submission, 'contractType') ?? engagement.onboardingType ?? record.contractType,
  );

  // Client Job Rate — jobs.jobPayDetails, else the submission's own client
  // budget. A range keeps the TO end (the ceiling we bill against).
  const pay = job.jobPayDetails ?? {};
  const clientJobRateFrom = num(pay.clientBudgetStart ?? pick(submission, 'clientBudget', 'clientBudgetStart'));
  const clientJobRateTo = num(pay.clientBudgetEnd ?? pick(submission, 'clientBudget', 'clientBudgetEnd')) || clientJobRateFrom;
  const clientJobRate = clientJobRateTo || clientJobRateFrom;

  // Discount (VMS) — Client form's "Account & Billing Setup" group now keeps
  // this as PROPER HISTORY (cmd/wire-client-vms-history): every save appends
  // {vmsPercentage, vmsPercentageType, timestamp, updatedBy} to
  // clients.vmsFeeHistory[] instead of overwriting two flat fields, so a rate
  // change never erases what the fee used to be. Fallback chain, most-current
  // first: vmsFeeHistory's LAST entry → the old flat vmsPercentage/
  // vmsPercentageType fields (clients saved between the form existing and this
  // history migration) → the legacy nested vmsFee object (clients that predate
  // the form group entirely and were never re-saved).
  const vmsFeeEntry = lastEntry(client.vmsFeeHistory) ?? {};
  const vmsFee = lookups.clientVmsFee ?? client.vmsFee ?? {};
  // vmsPercentage (however it's sourced) wins OUTRIGHT once it's actually set
  // — vmsFee.isApplicable must NOT gate it: that flag belongs to the legacy
  // object and is often stale/false on a client whose admin has since set a
  // real vmsPercentage (e.g. seeded false, never revisited). isApplicable only
  // gates the FINAL fallback, where vmsFee is the only source we have.
  const clientVmsPercent = hasValue(vmsFeeEntry.vmsPercentage)
    ? num(vmsFeeEntry.vmsPercentage)
    : hasValue(client.vmsPercentage)
      ? num(client.vmsPercentage)
      : vmsFee.isApplicable === false
        ? 0
        : num(firstDefined(
          vmsFee.value, vmsFee.percent,
          typeof vmsFee === 'number' ? vmsFee : undefined,
          pick(submission, 'clientVmsPercent'), pick(submission, 'vmsPercent'),
        ));
  // Billing cadence for the VMS discount — "recurring" (every cycle, the
  // long-standing default/behavior) or "onetime" (a single deduction). Purely
  // descriptive here: computeFinance/callers surface it for display; the
  // per-cycle amount math is unchanged for either type so existing totals
  // never move because of this field.
  // NOTE: vmsFee.type is "PERCENTAGE"/"FLAT" — the unit the legacy value is
  // in, NOT a billing cadence — so it is deliberately not a fallback here.
  const clientVmsPercentageType = String(
    firstDefined(vmsFeeEntry.vmsPercentageType, client.vmsPercentageType) ?? 'recurring',
  ).toLowerCase();

  // Proposed Rate — proposedRate history/object/dotted.
  const proposedRate = num(pick(submission, 'proposedRate', 'candidateProposedRate') ?? record.proposedRateValue);

  // Currency — resolved MOST-SPECIFIC-FIRST across every place a rate currency
  // can live, ending at the job (a submission created before the currency field
  // existed still bills in the job's currency, not in a guessed dollar).
  // Two values come out of this deliberately:
  //   currencyRaw — exactly what was stored ("USD" / "$" / "INR"). This is what
  //                 gets WRITTEN BACK on approve, so approving never rewrites a
  //                 tenant's ISO code into a symbol.
  //   currency    — the same value normalised through the shared
  //                 currencySymbol(), because the old code prepended the raw
  //                 value and a stored "USD" therefore printed "USD100".
  const currencyRaw = firstDefined(
    pick(submission, 'proposedRate', 'candidateProposedRateCurrencySymbol'),
    pick(submission, 'proposedRate', 'candidateProposedRateCurrency'),
    pick(submission, 'candidateBudget', 'candidateBudgetCurrencySymbol'),
    pick(submission, 'candidateBudget', 'candidateBudgetCurrency'),
    pay.clientBudgetCurrencySymbol,
    pay.clientBudgetCurrency,
    job.currency,
    job.currencySymbol,
    fallback.currency,
  );
  const currency = currencySymbol(currencyRaw, fallback.currency);

  // Rate unit — the period every rate on this submission is quoted in. Same
  // shape of chain: what the recruiter actually chose on the submission wins,
  // then the candidate budget's unit, then the job's own budget unit, then the
  // configured default. Nothing here assumes hours; "/hr" used to be hardcoded
  // in a dozen render sites AND written back on approve, which silently
  // overwrote a daily/monthly rate's unit with "hr".
  const rateUnit = firstDefined(
    pick(submission, 'proposedRate', 'candidateProposedRateUnit'),
    pick(submission, 'candidateBudget', 'candidateBudgetUnit'),
    pay.clientBudgetUnit,
    fallback.rateUnit,
  );

  // Employer Rate — candidateBudget (or candidateRate) history/object/dotted.
  const employerRate = num(
    pick(submission, 'candidateBudget', 'candidateBudgetStart')
    ?? pick(submission, 'candidateBudget', 'candidateBudgetRate')
    ?? pick(submission, 'candidateRate', 'candidateAcceptedRate'),
  );

  // Employer-side C2C VMS — submission_current_employer[].{isVmsRequired,
  // vmsPercentage, vmsType}, the fields the submission form itself writes
  // (Employer group, gated to C2C by showIf — see the Form Groups config).
  // Gated on isVmsRequired: when the recruiter left VMS unchecked, the
  // deduction is 0 even if a stale vmsPercentage sits on an older row —
  // "only if this boolean is true" is enforced HERE, not just at entry.
  const employerVmsRequired = truthy(
    pick(submission, 'submission_current_employer', 'isVmsRequired')
    ?? pick(submission, 'submission_current_employer', 'isVMSRequired'), // legacy key, pre-rename rows
  );
  // The percentage as CONFIGURED, independent of the checkbox. Kept separate
  // from employerVmsPercent (the one that feeds the math) because the UI has to
  // tell two different situations apart, and a single 0 cannot:
  //   • the employer HAS a VMS % but the recruiter unchecked "VMS Required"
  //     → "Not required" (a deliberate choice on this submission)
  //   • the employer has no VMS % configured anywhere at all
  //     → "VMS Not available" (nothing to apply, nothing was decided)
  // A configured 0% counts as *not available*: it deducts nothing and is
  // indistinguishable from "never set", so calling it "not required" would put
  // us back where we started.
  const employerVmsPercentConfigured = num(firstDefined(
    pick(submission, 'submission_current_employer', 'vmsPercentage'),
    pick(submission, 'submission_current_employer', 'vmsCommission'), // legacy key
    record.employer?.vmsPercentage,
    record.employer?.vmsCommission,
    engagement.vmsPercent,
  ));
  const employerVmsAvailable = employerVmsPercentConfigured > 0;
  const employerVmsPercent = employerVmsRequired ? employerVmsPercentConfigured : 0;
  const employerVmsType = String(
    pick(submission, 'submission_current_employer', 'vmsType')
    ?? pick(submission, 'submission_current_employer', 'vmsCommissionType') // legacy key
    ?? 'Recurring',
  );

  // W2 location tax — the employer row only carries locationId (tax itself is
  // payloadMode:"skip", never stored); lookups.locationTaxPercent is resolved
  // by financeApprovalData.js matching that id against the locations
  // collection. inlineTax/name-matching are fallbacks for rows saved before
  // that lookup existed.
  const inlineTax = pick(submission, 'submission_current_employer', 'tax');
  const locationTaxPercent = lookups.locationTaxPercent != null
    ? num(lookups.locationTaxPercent)
    : inlineTax != null && inlineTax !== ''
      ? num(inlineTax)
      : matchLocationTax(lookups.locationTaxRows,
        pick(submission, 'submission_current_employer', 'locationId')
        ?? pick(submission, 'submission_current_employer', 'location') // legacy key
        ?? pick(submission, 'city') ?? pick(submission, 'state') ?? job.jobLocation);

  return {
    contractType,
    currency,
    currencyRaw,
    rateUnit,
    clientJobRate,
    clientJobRateFrom,
    clientJobRateTo,
    clientVmsPercent,
    clientVmsPercentageType,
    proposedRate,
    employerRate,
    employerVmsPercent,
    employerVmsPercentConfigured,
    employerVmsAvailable,
    employerVmsType,
    employerVmsRequired,
    locationTaxPercent,
  };
}

// matchLocationTax — find the taxValue for a location inside an
// employerLocationTax.locationTax[] rows array. Case-insensitive contains
// match on the location string; 0 when nothing matches.
export function matchLocationTax(rows, location) {
  if (!Array.isArray(rows) || !location) return 0;
  const needle = String(location).trim().toLowerCase();
  const hit = rows.find((row) => {
    const loc = String(row?.location ?? '').trim().toLowerCase();
    return loc && (loc === needle || needle.includes(loc) || loc.includes(needle));
  });
  return num(hit?.taxValue ?? hit?.taxPercent);
}

// ── The calculation ─────────────────────────────────────────────────────────
// computeFinance(inputs, { referralRate, includeReferral, deductReferral }) → every derived
// line the UI renders, all rounded to cents. `overrides` lets a surface swap
// in a live-edited value (submissionManager's editable proposed rate) without
// re-resolving the whole record.
export function computeFinance(inputs, options = {}) {
  const {
    contractType = CONTRACT_C2C,
    currency: currencyInput = ENGINE_DEFAULTS.currency,
    currencyRaw,
    rateUnit = ENGINE_DEFAULTS.rateUnit,
    clientJobRate = 0,
    clientJobRateFrom = 0,
    clientJobRateTo = 0,
    clientVmsPercent = 0,
    clientVmsPercentageType = 'recurring',
    proposedRate: baseProposed = 0,
    employerRate = 0,
    employerVmsPercent = 0,
    employerVmsPercentConfigured = 0,
    employerVmsAvailable = false,
    employerVmsType = 'Recurring',
    employerVmsRequired = false,
    locationTaxPercent = 0,
  } = inputs ?? {};

  // Normalise here too, not only in resolveFinanceInputs: computeFinance is
  // called directly (tests, surfaces that assemble their own inputs), and a
  // raw "USD" reaching a formatter is exactly the bug this guards.
  const currency = currencySymbol(currencyInput, ENGINE_DEFAULTS.currency);

  const proposedRate = options.proposedRate != null
    ? num(options.proposedRate)
    : num(baseProposed);
  const includeReferral = Boolean(options.includeReferral);
  const referralRate = includeReferral ? num(options.referralRate) : 0;
  // A one-time referral payout still belongs to the finance record and must be
  // displayed/persisted, but it is not a recurring rate cost and therefore
  // must not reduce the per-cycle margin. `deductReferral` is separate from
  // `includeReferral` for exactly that reason. Existing callers keep the old
  // behaviour because the default mirrors includeReferral.
  const deductReferral = options.deductReferral == null
    ? includeReferral
    : Boolean(options.deductReferral);
  const referralDeduction = deductReferral ? referralRate : 0;

  // Client side (context strip).
  const clientDiscount = pct(clientJobRate, clientVmsPercent);
  const netClientRate = round2(clientJobRate - clientDiscount);

  // Proposed side (margin).
  const discountVms = pct(proposedRate, clientVmsPercent);
  const netProposedRate = round2(proposedRate - discountVms);

  // Employer side (margin) — contract-type dependent deduction.
  const isW2 = contractType === CONTRACT_W2;
  const employerDeductionPercent = isW2 ? locationTaxPercent : employerVmsPercent;
  const employerDeduction = pct(employerRate, employerDeductionPercent);
  const netEmployerRate = round2(employerRate - employerDeduction);

  const totalMargin = round2(netProposedRate - netEmployerRate - referralDeduction);

  return {
    contractType,
    currency,
    // What to write back on approve — the stored form, never the symbol we
    // derived for display.
    currencyRaw: currencyRaw ?? currency,
    rateUnit,
    rateUnitLabel: unitLabel(rateUnit),
    rateUnitShort: unitShort(rateUnit),
    isW2,
    // client context
    clientJobRate: round2(clientJobRate),
    clientJobRateFrom: round2(clientJobRateFrom),
    clientJobRateTo: round2(clientJobRateTo),
    clientVmsPercent: num(clientVmsPercent),
    clientVmsPercentageType,
    clientDiscount,
    netClientRate,
    // proposed
    proposedRate: round2(proposedRate),
    discountVms,
    netProposedRate,
    // employer
    employerRate: round2(employerRate),
    employerDeductionPercent: num(employerDeductionPercent),
    employerDeduction,
    employerDeductionLabel: isW2 ? 'Location Tax' : 'Employer VMS',
    employerVmsType,
    employerVmsRequired: Boolean(employerVmsRequired),
    employerVmsPercentConfigured: num(employerVmsPercentConfigured),
    employerVmsAvailable: Boolean(employerVmsAvailable),
    netEmployerRate,
    // referral + outcome
    includereferral: includeReferral,
    referralRate: round2(referralRate),
    referralDeduction: round2(referralDeduction),
    totalMargin,
    marginPositive: totalMargin >= 0,
  };
}

// resolveAndCompute — convenience one-shot used by read-only surfaces (cfo,
// onboarding detail) that don't edit inline: resolve inputs off the record
// then compute. Editable surfaces call resolveFinanceInputs once and
// computeFinance on every keystroke with an override instead.
export function resolveAndCompute(record, lookups, options = {}) {
  return computeFinance(resolveFinanceInputs(record, lookups, options.defaults), options);
}

// ── Numeric input policy (config-driven, pure) ───────────────────────────────
// A rate input's rules live in financeApprovalConfig
// (sections.rateBreakdown.proposed.policy = { decimals, min, max, step }); these
// helpers turn that declaration into the three checks a text field needs, with
// no policy value hardcoded — flipping `decimals` to 0 gives an integer-only
// field, and to 3 a 3-decimal one, with no component change.
export const DEFAULT_NUMERIC_POLICY = { decimals: 2, min: 0 };

// countDecimals('62.5') → 1. Exponent forms ("1e-3") are treated as
// out-of-policy rather than expanded: nobody types one into a rate box, and
// silently accepting it would sneak past the decimal cap.
export function countDecimals(value) {
  const text = String(value ?? '').trim();
  if (!text || /e/i.test(text)) return text ? Infinity : 0;
  const dot = text.indexOf('.');
  return dot === -1 ? 0 : text.length - dot - 1;
}

// isWithinPolicy — does this COMPLETE value satisfy the policy? Used for the
// inline error (a paste bypasses every keystroke guard, so the value itself
// must be checked as well).
export function isWithinPolicy(value, policy = {}) {
  const { decimals = DEFAULT_NUMERIC_POLICY.decimals, min, max } = { ...DEFAULT_NUMERIC_POLICY, ...policy };
  if (value === null || value === undefined || value === '') return true;
  const text = String(value).trim();
  if (!/^-?\d*(\.\d*)?$/.test(text)) return false;
  if (countDecimals(text) > num(decimals)) return false;
  const n = Number(text);
  if (!Number.isFinite(n)) return false;
  if (min !== undefined && min !== null && n < num(min)) return false;
  if (max !== undefined && max !== null && n > num(max)) return false;
  return true;
}

// Keys that edit or navigate rather than insert a character — never blocked,
// otherwise the field becomes impossible to correct.
const CONTROL_KEYS = new Set([
  'Backspace', 'Delete', 'Tab', 'Enter', 'Escape', 'Home', 'End',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown',
]);

// acceptsKey — would this keystroke leave the field holding a value the policy
// still permits? Returns false for a non-numeric character, for a second
// decimal point, for a minus sign when negatives aren't allowed (min >= 0), and
// — the reason this exists — for the digit that would become a 3rd decimal.
//
// `text`/`start`/`end` describe the field BEFORE the key: we splice the key in
// at the caret (replacing any selection) and test the resulting string, so
// typing in the middle of an existing number is judged on the real outcome
// rather than on the key alone.
export function acceptsKey(policy = {}, key, text = '', start = null, end = null) {
  if (CONTROL_KEYS.has(key) || String(key ?? '').length !== 1) return true;
  const { decimals = DEFAULT_NUMERIC_POLICY.decimals, min } = { ...DEFAULT_NUMERIC_POLICY, ...policy };
  const current = String(text ?? '');
  const from = start ?? current.length;
  const to = end ?? from;
  const next = current.slice(0, from) + key + current.slice(to);
  const allowNegative = min === undefined || min === null || num(min) < 0;
  if (key === '-') return allowNegative && /^-?\d*(\.\d*)?$/.test(next);
  if (key === '.') return num(decimals) > 0 && /^-?\d*(\.\d*)?$/.test(next);
  if (!/^\d$/.test(key)) return false;
  if (!/^-?\d*(\.\d*)?$/.test(next)) return false;
  return countDecimals(next) <= num(decimals);
}

// clampToPolicy — the value actually committed on change: rounded to the
// allowed number of decimals and pulled inside min/max.
export function clampToPolicy(value, policy = {}) {
  const { decimals = DEFAULT_NUMERIC_POLICY.decimals, min, max } = { ...DEFAULT_NUMERIC_POLICY, ...policy };
  let n = num(value);
  const factor = 10 ** num(decimals);
  n = Math.round((n + Number.EPSILON) * factor) / factor;
  if (min !== undefined && min !== null) n = Math.max(n, num(min));
  if (max !== undefined && max !== null) n = Math.min(n, num(max));
  return n;
}

// ── Audit-log helper (workforceFinanceApprovals.rateHistory[]) ───────────────
// buildRateHistoryEntries — turn a computed snapshot into the timestamped
// {field, value, updatedBy, timestamp} rows persisted on save, so every rate,
// VMS, tax and referral change is attributable. The backend $push-es these
// onto workforceFinanceApprovals.rateHistory (see plan phase 4).
export function buildRateHistoryEntries(computed, updatedBy, at = new Date()) {
  const timestamp = at instanceof Date ? at.toISOString() : at;
  const fields = [
    ['proposedRate', computed.proposedRate],
    ['discountVms', computed.discountVms],
    ['netProposedRate', computed.netProposedRate],
    ['employerRate', computed.employerRate],
    [computed.isW2 ? 'locationTax' : 'employerVms', computed.employerDeduction],
    ['netEmployerRate', computed.netEmployerRate],
    ['referralRate', computed.referralRate],
    ['totalMargin', computed.totalMargin],
  ];
  return fields.map(([field, value]) => ({
    field, value, updatedBy, timestamp,
  }));
}

// formatMoney — kept as an export so every existing caller keeps working, but
// it is now a THIN DELEGATE to the package-wide utils/currency formatter. It
// used to prepend `currency` verbatim (so a stored "USD" printed "USD100") and
// skip thousands separators, which is how the finance panel and the detail view
// ended up disagreeing about the same number. One implementation, one answer.
// The `?? 0` keeps this surface's long-standing contract that a blank/absent
// rate reads as "$0.00" (the shared util deliberately passes unparseable text
// through untouched, which is right for a free-text detail field but would
// leave a hole in a rate column).
export function formatMoney(value, currency = ENGINE_DEFAULTS.currency) {
  const n = toNumber(value);
  return formatMoneyShared(Number.isNaN(n) ? 0 : n, currency, { decimals: 2 });
}

// formatRate — an amount with its unit suffix ("$62.50/hr"). The unit is
// whatever the record resolved to; an empty unit yields the bare amount rather
// than a dangling slash.
export function formatRate(value, currency = ENGINE_DEFAULTS.currency, unit) {
  const short = unitShort(unit, '');
  return short ? `${formatMoney(value, currency)}/${short}` : formatMoney(value, currency);
}
